import {isEmpty, isEqual, isNull, isUndefined} from "lodash/lang"
import {has} from "lodash/object"
import {difference, head, remove} from "lodash/array"
import UploadFileHolder, {isUploadFileHolder} from "../upload/file-holder"
import {apiAuth} from "../../main"
import {NetworkError, normalizeError} from "../errors"
import notify, {NOTIFICATION_TYPES, NotificationOptions} from "../notify"
import dialog, {DIALOG_TYPES, DialogOptions} from "../dialogs"
import {filter, includes} from "lodash/collection"
import {deleteFlightOrderUploadedImage} from "../../api/api-project"
import {getFileExtension, readFileAsync} from "../utils"
import {imageMimeTypes} from "../upload/mime-types"
import imageType from "image-type"
import UTIF from "utif"
import EXIF from "exif-js"
import {GEOREFERENCE_TYPES} from "../processings/constants"
import {IMAGES_PER_PAGE} from "../upload/constants"
import {deleteFlightOrderAttachment} from "../../api/api-flight-provider"

export class ImageUploader {

  constructor(uploadUrl, idProcessing, changePageCallback = null, onCompleteCallback = null) {
    this._toUploadQueue = []
    this._uploadedQueue = []

    this._complete = false
    this._uploading = false
    this._evaluating = false
    this._loading = false
    this._deleting = false

    this._totalEvaluated = 0
    this._totalToEvaluate = 0

    this._progress = 0
    this.uploadUrl = uploadUrl
    this.idProcessing = idProcessing

    this.retrySetTimeout = null

    const noopFunction = () => {
    }
    this.changePageCallback = changePageCallback || noopFunction
    this.onCompleteCallback = onCompleteCallback || noopFunction
  }

  start() {
    this._uploading = true
    this.next()
  }

  next() {
    if (isEmpty(this._toUploadQueue)) return
    const uploadFileHolder = new UploadFileHolder(
      head(this._toUploadQueue), this.onProgress, this.onComplete, this.onError, this
    )
    this._toUploadQueue.splice(0, 1, uploadFileHolder)
    this.upload(uploadFileHolder)
  }

  upload(uploadFileHolder) {
    if (!uploadFileHolder) {
      notify(new NotificationOptions({
        message: "Nenhum arquivo encontrado, por favor insira um arquivo para continuar.",
        type: NOTIFICATION_TYPES.WARNING,
        duration: 15000,
      }))
      return
    }
    uploadFileHolder.startUpload(
      this.uploadUrl, `Bearer ${apiAuth.getAuthToken()}`, null, this.idProcessing
    )

    this._uploading = true
  }

  add(files, isMultispectral, checkGeotag, hasFlightlog) {
    const verifyImagesIntegrity = async (_files) => {
      async function loadFileContent(_file, sliceSize) {
        let fileData
        if (sliceSize) fileData = new Blob([_file]).slice(0, sliceSize)
        else fileData = new Blob([_file])
        let contentBuffer = null
        try {
          contentBuffer = await readFileAsync(fileData)
        } catch (err) {
          console.error(err)
        }
        return contentBuffer
      }

      this._totalEvaluated = 0
      this._totalToEvaluate = _files.length

      let checkImagesStep = null
      if (isMultispectral && checkGeotag) checkImagesStep = 100

      const filesData = []
      for (const file of files) {
        if (file.size > (300 * 1000000)) {
          // Se a imagem for mais de 300 Mb então a checagem acontece de forma superficial,
          //  sem carregar a imagem, isso aqui ainda acontece pois algumas coisas estranhas
          //  aconteceram com imagens tiffs maiores que 1Gb, algumas vezes elas não eram
          //  detectadas pela função imageType, então usa esse fallback por enquanto.
          const fileType = file.type
          const fileExtension = getFileExtension(file.name)

          if (!imageMimeTypes.includes(fileType)) {
            file.isImage = false
            file.isGeotagged = false
            filesData.push(file)
            continue
          }

          file.imageType = {
            mime: fileType,
            ext: fileExtension,
          }
          file.isImage = true
          file.isGeotagged = false
          if (fileExtension == "zip") {
            file.isZip = true
            file.isGeotagged = true
          }
          filesData.push(file)
          continue
        }
        const contentBuffer = await loadFileContent(file, Math.pow(2, 16))
        let fileType = imageType(new Uint8Array(contentBuffer))

        if (fileType) {
          file.isImage = true
          if (checkGeotag) {
            if (fileType.mime === "image/tiff") {
              // Se for multispectral apenas checa uma em cada 10 imagens pelo GeoTag
              if (checkImagesStep) {
                if (this.intImagesEvaluated % checkImagesStep === 0) {
                  const tiffContentBuffer = await loadFileContent(file)
                  const ifds = UTIF.decode(tiffContentBuffer)
                  file.isGeotagged = has(ifds[0], "t34853")
                } else {
                  file.isGeotagged = true
                }
              }
            } else {
              const exifTags = EXIF.readFromBinaryFile(contentBuffer)
              file.isGeotagged =
                !isEmpty(exifTags["GPSLatitude"]) &&
                !isNull(exifTags["GPSLatitude"]) &&
                !isUndefined(exifTags["GPSLatitude"])
            }
          }
        } else if(file.name.split('.').pop() === 'zip'){
          file.isImage = true
          file.isGeotagged = true
          fileType = file.type
          file.isZip = true
        } else {
          file.isImage = false
          file.isGeotagged = false
        }

        file.imageType = fileType
        filesData.push(file)
        this.intImagesEvaluated++
      }

      return filesData
    }

    const onEvaluateComplete = (filesData) => {
      let imageFiles = filter(filesData, value => value["isImage"])
      let fileName = '';
      let isZipFile = false

      if (!isEmpty(filesData) && filesData[0] && !isEmpty(filesData[0].name)) {
        fileName = filesData[0].name;
        isZipFile = fileName.includes('.zip') ? true : false;
      }

      let nonImageFiles = filter(filesData, value => !value["isImage"])
      let zipTypes = ["application/zip", "application/x-zip-compressed", "application/octet-stream", "application/x-compress", "application/x-compressed", "multipart/x-zip"]
      let nonRGBFiles;

      if (!isZipFile) {
        if (isMultispectral) {
          // Checagem de imagens que não são .tif
          let nonValidFiles = filter(imageFiles, (image) => !["image/tiff", "image/jpeg"].includes(image.imageType.mime))
          nonValidFiles = filter(nonValidFiles, (image) => !zipTypes.includes(image.type))
          if (!isEmpty(nonValidFiles)) {
            const filesName = nonValidFiles.map(file => "- " + file.name).join("<br>")
            dialog(new DialogOptions({
              title: "Arquivo(s) não detectado(s) como .tif:",
              content: `${filesName}`,
              confirmText: "Entendi",
              type: DIALOG_TYPES.ALERT,
            }))
          }
          imageFiles = difference(imageFiles, nonValidFiles)
        } else {
          // Checagem de imagens que não são .png ou .jpg
          nonRGBFiles = filter(imageFiles, (image) => !["image/png", "image/jpeg"].includes(image.imageType.mime))
          nonRGBFiles = filter(nonRGBFiles, (image) => !zipTypes.includes(image.type))
          if (!isEmpty(nonRGBFiles)) {
            const filesName = nonRGBFiles.map(file => "- " + file.name).join("<br>")
            dialog(new DialogOptions({
              title: "Arquivo(s) não detectado(s) como .png ou .jpeg:",
              content: `${filesName}`,
              confirmText: "Entendi",
              type: DIALOG_TYPES.ALERT,
            }))
          }
        }
        imageFiles = difference(imageFiles, nonRGBFiles)
        nonImageFiles = filter(nonImageFiles, (image) => !zipTypes.includes(image.type))
      }
      let allFiles = filesData.concat(this.uploadedImages, this.toUploadImages).filter(ele => {
        return ele
      })
      let hasError = false
      let zipfiles = 0
      if (allFiles.length>1){
        allFiles.forEach(val => {
          if(val && val.isZip){
            zipfiles = zipfiles + 1
          }
        })
      }
      if(zipfiles>1){
        hasError=true
      }
      if (hasError){
        const filesName = allFiles.map(file => {if(file) return file.name}).join(", ")
        dialog(new DialogOptions({
          title: "É possível o envio de apenas um arquivo compactado, arquivos encontrados: ",
          content: `${filesName}`,
          confirmText: "Entendi",
          type: DIALOG_TYPES.ALERT,
        }))
        imageFiles = difference(imageFiles, allFiles)
      }
      // Avisa o usuario safado que ele tá fazendo safadagem
      if (!isEmpty(nonImageFiles) && !isZipFile) {
        const filesName = nonImageFiles.map(file => file.name).join(", ")
        dialog(new DialogOptions({
          title: "Arquivo(s) não detectado(s) como imagens:",
          content: `${filesName}`,
          confirmText: "Entendi",
          type: DIALOG_TYPES.ALERT,
        }))
      }

      if (isEmpty(imageFiles) && !isZipFile) {
        this._evaluating = false
        return
      }

      if (!hasFlightlog && !isZipFile) {
        const nonGeotaggedImages = filter(imageFiles, value => !value["isGeotagged"])

        // Avisa que o usuario é burro
        if (!isEmpty(nonGeotaggedImages)) {
          if (!isMultispectral) {
            const filesName = nonGeotaggedImages.map(file => file.name).join(", ")
            dialog(new DialogOptions({
              title: "Arquivo(s) detectado(s) sem georreferenciamento:",
              content: `${filesName}`,
              confirmText: "Entendi",
              type: DIALOG_TYPES.ALERT,
            }))
          } else {
            dialog(new DialogOptions({
              title: "Erro ao adicionar arquivo(s)",
              content: "Algumas de suas imagens não tiveram georreferenciamento detectado. <br> " +
                "Por favor verifique o geotag de suas imagens e faça o upload novamente",
              confirmText: "Entendi",
              type: DIALOG_TYPES.ALERT,
            }))
            this._evaluating = false
            return
          }

          imageFiles = filter(filesData, ["isGeotagged", true])
        }
      }

      if(isZipFile) {
        imageFiles = filesData;
      }
      this._toUploadQueue = [...this.toUploadQueue, ...imageFiles]
      this._evaluating = false
    }

    if (!files) return
    this._evaluating = true

    // Filtra os arquivos com nomes repetidos
    const allFileNames = [
      ...this._toUploadQueue.map((obj) => obj.name),
      ...this._uploadedQueue.map((obj) => obj.name),
    ]
    const newFileNames = files.map((obj) => obj.name)
    const intersectedFilesName = allFileNames.filter(name => newFileNames.includes(name))

    files = files.filter((obj) => !intersectedFilesName.includes(obj.name))

    if (!isEmpty(intersectedFilesName)) {
      dialog(new DialogOptions({
        title: "Imagens já adicionadas",
        content: `As seguintes imagens já foram adicionadas ao upload: <br> ${intersectedFilesName.join("<br>")}`,
        cancelText: "Ok, Entendi",
      }))
    }

    if (isEmpty(files)) {
      this._evaluating = false
      return
    }

    verifyImagesIntegrity(files)
      .then(filesData => {
        onEvaluateComplete(filesData)
      })
  }

  remove(uploadFileHolder) {
    if (isUploadFileHolder(uploadFileHolder) && uploadFileHolder.uploadCompleted) {
      dialog(new DialogOptions({
        title: "Tem certeza?",
        content: `Você tem certeza que deseja deletar a image ${uploadFileHolder.name}?`,
        confirmText: "SIM, DELETAR IMAGE",
        cancelText: "CANCELAR",
        type: DIALOG_TYPES.CONFIRM,
        onConfirm: () => {
          this._deleting = true
          deleteFlightOrderUploadedImage(apiAuth.getAuthToken(), this.idProcessing, uploadFileHolder.name)
            .then(() => {
              this._uploadedQueue = filter(this._uploadedQueue, obj => !isEqual(uploadFileHolder.name, obj.name))
              this._deleting = false

              if (isEmpty(this._uploadedQueue)) this._progress = 0
              else this.onProgress(0)
            })
            .catch((err) => {
              notify(new NotificationOptions({
                message: `Ocorreu um erro ao remover o arquivo ${uploadFileHolder.name}. Cod.: ${err.response.data.code}`,
                type: NOTIFICATION_TYPES.ERROR,
              }))
              this.isDeletingImage = false
            })
        },
      }))
    } else {
      this._toUploadQueue = filter(this._toUploadQueue, obj => !isEqual(uploadFileHolder.name, obj.name))
    }
  }

  onProgress(progress) {
    const totalLength = [...this._toUploadQueue, ...this._uploadedQueue].length

    let currIdx = this._uploadedQueue.length

    let totalProgress = Math.ceil(progress / totalLength + (currIdx * (100 / totalLength)))
    if (totalProgress >= 100) this._progress = 100
    else this._progress = totalProgress
  }

  onError(error, uploadFileHolder) {
    const normErr = normalizeError(error)
    if (includes([500, 503], normErr.statusCode)) {
      notify(new NotificationOptions({
        message: "Ocorreu um erro durante seu upload, por favor, tente novamente mais tarde. Se o problema persistir entre em contato conosco pelo chat.",
        duration: 4 * 3600 * 1000,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      this._toUploadQueue[0].uploadProgress = 0
      this._toUploadQueue[0].uploadStarted = false
      this._toUploadQueue[0].uploadCompleted = false
      this._uploading = false
    } else if (normErr instanceof NetworkError) {
      const retrySecs = 10
      notify(new NotificationOptions({
        message: `Foi detectado uma falha de conexão com a internet.
          O sistema tentará reconectar em ${retrySecs} segundos.
          Por favor, verifique sua internet.`,
        duration: (retrySecs * 1000) - 200,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      if (this.retrySetTimeout) clearTimeout(this.retrySetTimeout)
      this.retrySetTimeout = setTimeout(() => {
        this.upload(uploadFileHolder)
      }, retrySecs * 1000)
    }
  }

  onComplete(uploadFileHolder) {
    remove(this._toUploadQueue, obj => isEqual(uploadFileHolder.name, obj.name))
    this._uploadedQueue.push(uploadFileHolder)

    if (isEmpty(this._toUploadQueue)) {
      this._uploading = false
      this.onCompleteCallback()
      return
    }

    if (this._uploadedQueue.length % IMAGES_PER_PAGE === 0)
      this.changePageCallback((this._uploadedQueue.length / IMAGES_PER_PAGE))

    this.next()
  }

  //  Getters
  get toUploadQueue() {
    return this._toUploadQueue
  }

  get complete() {
    return this._complete
  }

  get uploading() {
    return this._uploading
  }

  get evaluating() {
    return this._evaluating
  }

  get loading() {
    return this._loading
  }

  get deleting() {
    return this._deleting
  }

  get totalEvaluated() {
    return this._totalEvaluated
  }

  get totalToEvaluate() {
    return this._totalToEvaluate
  }

  get progress() {
    return this._progress
  }

  get uploadedQueue() {
    return this._uploadedQueue
  }

  get canUpload() {
    return !(this.uploading || this.loading)
  }

  get fullImagesList() {
    return [...this.uploadedQueue, ...this.toUploadQueue]
  }

  get isFinishingStream() {
    return this.uploading && this.progress >= 100 && this.toUploadQueue.length === 1
  }

  // Setters

  set progress(value) {
    this._progress = value
  }

  set uploadedQueue(value) {
    this._uploadedQueue = value
  }

  set loading(value) {
    this._loading = value
  }
}

export class FlightLogUploader {

  constructor(uploadUrl, abGeoreferenceType, onCompleteCallback = null) {
    this._uploadFileHolder = null
    this._complete = false
    this._uploading = false
    this._progress = 0
    this.uploadUrl = uploadUrl

    this.abGeoreferenceType = abGeoreferenceType

    this.retrySetTimeout = null

    const noopFunction = () => {
    }
    this.onCompleteCallback = onCompleteCallback || noopFunction

    this.tmpAbGeoreferenceType = null
  }

  upload() {
    if (!this._uploadFileHolder) {
      notify(new NotificationOptions({
        message: "Nenhum arquivo encontrado, por favor insira um arquivo para continuar.",
        type: NOTIFICATION_TYPES.WARNING,
        duration: 15000,
      }))
      return
    }
    let fileName
    if (isEqual(GEOREFERENCE_TYPES.THREE_D_ROBOTICS, this.abGeoreferenceType)) fileName = "flight_log.log"
    else fileName = "flight_log.csv"

    this._uploadFileHolder.startUpload(
      this.uploadUrl, `Bearer ${apiAuth.getAuthToken()}`, fileName,
    )

    this._uploading = true
  }

  add(file) {
    if (!file) return
    if (this._complete) {
      if (isEqual(file.name, this._uploadFileHolder.name)) return

      this._complete = false
      this._progress = 0
    }

    let fileExt = null
    if (isEqual(this.abGeoreferenceType, GEOREFERENCE_TYPES.THREE_D_ROBOTICS))
      fileExt = ".log"
    else if ([GEOREFERENCE_TYPES.LAT_LONG, GEOREFERENCE_TYPES.LONG_LAT].includes(this.abGeoreferenceType))
      fileExt = ".txt"
    else return // shhhhh

    if (file && !file.name.endsWith(fileExt)) {
      let message = null
      if ([GEOREFERENCE_TYPES.LAT_LONG, GEOREFERENCE_TYPES.LONG_LAT].includes(this.abGeoreferenceType))
        message = "Erro no log de voo: formato esperado .txt. " +
          "O formato de arquivo não é válido com o tipo de log de voo especificado no projeto. "
      else if (file.name.endsWith(".bin"))
        message = "Erro no log de voo: formato esperado .log. " +
          "Foi identificado que o log d e voo adicionado é um arquivo no formato .bin, por favor, converta-o para .log."
      else
        message = "Erro no log de voo: formato esperado .log. " +
          "O formato de arquivo não é válido com o tipo de log de voo especificado no projeto. "

      notify(new NotificationOptions({
        message: message,
        duration: 15000,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      this._uploadFileHolder = null
      return
    }
    this._uploadFileHolder = new UploadFileHolder(
      file, this.onProgress, this.onComplete, this.onError, this
    )
  }

  onProgress(progress) {
    this._progress = Math.trunc(progress)
  }

  onError(error) {
    const normErr = normalizeError(error)
    if (includes([500, 503], normErr.statusCode)) {
      notify(new NotificationOptions({
        message: "Ocorreu um erro durante seu upload, por favor, tente novamente mais tarde. Se o problema persistir entre em contato conosco pelo chat.",
        duration: 4 * 3600 * 1000,
        type: NOTIFICATION_TYPES.WARNING,
      }))
    } else if (normErr instanceof NetworkError) {
      const retrySecs = 10
      notify(new NotificationOptions({
        message: `Foi detectado uma falha de conexão com a internet.
          O sistema tentará reconectar em ${retrySecs} segundos.
          Por favor, verifique sua internet.`,
        duration: (retrySecs * 1000) - 200,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      if (this.retrySetTimeout) clearTimeout(this.retrySetTimeout)
      this.retrySetTimeout = setTimeout(function () {
        this.upload()
      }.bind(this), retrySecs * 1000)
    }
  }

  onComplete() {
    this._uploading = false
    this._complete = true
    this.onCompleteCallback()
  }

  //  Getters

  get uploadFileHolder() {
    return this._uploadFileHolder
  }

  get complete() {
    return this._complete
  }

  get uploading() {
    return this._uploading
  }

  get progress() {
    return this._progress
  }

  get canUpload() {
    return (this.uploadFileHolder && !this.uploading && !this.complete)
  }

  // Setters

  set uploadFileHolder(value) {
    this._uploadFileHolder = value
  }

  set complete(value) {
    this._complete = value
  }

  set progress(value) {
    this._progress = value
  }
}

export class GcpUploader {

  constructor(uploadUrl, onCompleteCallback = null) {
    this._uploadFileHolder = null
    this._complete = false
    this._uploading = false
    this._progress = 0
    this.uploadUrl = uploadUrl

    this.retrySetTimeout = null

    const noopFunction = () => {
    }
    this.onCompleteCallback = onCompleteCallback || noopFunction
  }

  upload() {
    if (!this._uploadFileHolder) {
      notify(new NotificationOptions({
        message: "Nenhum arquivo encontrado, por favor insira um arquivo para continuar.",
        type: NOTIFICATION_TYPES.WARNING,
        duration: 15000,
      }))
      return
    }
    this._uploadFileHolder.startUpload(
      this.uploadUrl, `Bearer ${apiAuth.getAuthToken()}`, "gcp.txt",
    )

    this._uploading = true
  }

  add(file) {
    if (!file) return
    if (this._complete) {
      if (isEqual(file.name, this._uploadFileHolder.name)) return

      this._complete = false
      this._progress = 0
    }
    if (!file.name.endsWith(".txt")) {
      notify(new NotificationOptions({
        message: "Erro em pontos de controle: formato esperado .txt. " +
          "O formato de arquivo dos pontos de controle não é válido.",
        type: NOTIFICATION_TYPES.WARNING,
        duration: 15000,
      }))
      this._uploadFileHolder = null
      return
    }
    this._uploadFileHolder = new UploadFileHolder(
      file, this.onProgress, this.onComplete, this.onError, this
    )
  }

  onProgress(progress) {
    this._progress = Math.trunc(progress)
  }

  onError(error) {
    const normErr = normalizeError(error)
    if (includes([500, 503], normErr.statusCode)) {
      notify(new NotificationOptions({
        message: "Ocorreu um erro durante seu upload, por favor, tente novamente mais tarde. Se o problema persistir entre em contato conosco pelo chat.",
        duration: 4 * 3600 * 1000,
        type: NOTIFICATION_TYPES.WARNING,
      }))
    } else if (normErr instanceof NetworkError) {
      const retrySecs = 10
      notify(new NotificationOptions({
        message: `Foi detectado uma falha de conexão com a internet.
          O sistema tentará reconectar em ${retrySecs} segundos.
          Por favor, verifique sua internet.`,
        duration: (retrySecs * 1000) - 200,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      if (this.retrySetTimeout) clearTimeout(this.retrySetTimeout)
      this.retrySetTimeout = setTimeout(() => {
        this.upload()
      }, retrySecs * 1000)
    }
  }

  onComplete() {
    this._uploading = false
    this._complete = true
    this.onCompleteCallback()
  }

  //  Getters
  get uploadFileHolder() {
    return this._uploadFileHolder
  }

  get complete() {
    return this._complete
  }

  get uploading() {
    return this._uploading
  }

  get progress() {
    return this._progress
  }

  get canUpload() {
    return (this.uploadFileHolder && !this.uploading && !this.complete)
  }

  // Setters

  set uploadFileHolder(value) {
    this._uploadFileHolder = value
  }

  set complete(value) {
    this._complete = value
  }

  set progress(value) {
    this._progress = value
  }
}

export class FileUploader {

  constructor(uploadUrl, idOrder, changePageCallback = null, onCompleteCallback = null) {
    this._toUploadQueue = []
    this._uploadedQueue = []

    this._complete = false
    this._uploading = false
    this._evaluating = false
    this._loading = false
    this._deleting = false

    this._progress = 0
    this.uploadUrl = uploadUrl
    this.idOrder = idOrder

    this.retrySetTimeout = null

    const noopFunction = () => {
    }
    this.changePageCallback = changePageCallback || noopFunction
    this.onCompleteCallback = onCompleteCallback || noopFunction
  }

  start() {
    this._uploading = true
    this.next()
  }

  next() {
    if (isEmpty(this._toUploadQueue)) return
    const uploadFileHolder = new UploadFileHolder(
      head(this._toUploadQueue), this.onProgress, this.onComplete, this.onError, this
    )
    this._toUploadQueue.splice(0, 1, uploadFileHolder)
    this.upload(uploadFileHolder)
  }

  upload(uploadFileHolder) {
    if (!uploadFileHolder) {
      notify(new NotificationOptions({
        message: "Nenhum arquivo encontrado, por favor insira um arquivo para continuar.",
        type: NOTIFICATION_TYPES.WARNING,
        duration: 15000,
      }))
      return
    }
    uploadFileHolder.startUpload(
      this.uploadUrl, `Bearer ${apiAuth.getAuthToken()}`,
    )

    this._uploading = true
  }

  add(files) {
    if (!files) return
    // Filtra os arquivos com nomes repetidos
    const allFileNames = [
      ...this._toUploadQueue.map((obj) => obj.name),
      ...this._uploadedQueue.map((obj) => obj.name),
    ]
    const newFileNames = files.map((obj) => obj.name)
    const intersectedFilesName = allFileNames.filter(name => newFileNames.includes(name))

    files = files.filter((obj) => !intersectedFilesName.includes(obj.name))

    if (!isEmpty(intersectedFilesName)) {
      dialog(new DialogOptions({
        title: "Arquivos já adicionados",
        content: `Os seguintes arquivos já foram adicionados ao upload: <br> ${intersectedFilesName.join("<br>")}`,
        cancelText: "Ok, Entendi",
      }))
    }

    if (isEmpty(files)) {
      return
    }

    this._toUploadQueue = [...this._toUploadQueue, ...files]
  }

  remove(uploadFileHolder) {
    if (isUploadFileHolder(uploadFileHolder) && uploadFileHolder.uploadCompleted) {
      dialog(new DialogOptions({
        title: "Tem certeza?",
        content: `Você tem certeza que deseja deletar o arquivo ${uploadFileHolder.name}?`,
        confirmText: "Sim, deletar arquivo",
        cancelText: "Cancelar",
        type: DIALOG_TYPES.CONFIRM,
        onConfirm: () => {
          this._deleting = true
          const deleteData = {
            str_file_name: uploadFileHolder.name
          }
          deleteFlightOrderAttachment(apiAuth.getAuthToken(), this.idOrder, deleteData)
            .then(() => {
              this._uploadedQueue = filter(this._uploadedQueue, obj => !isEqual(uploadFileHolder.name, obj.name))
              this._deleting = false

              if (isEmpty(this._uploadedQueue)) this._progress = 0
              else this.onProgress(0)
            })
            .catch((err) => {
              notify(new NotificationOptions({
                message: `Ocorreu um erro ao remover o arquivo ${uploadFileHolder.name}. Cod.: ${err.response.data.code}`,
                type: NOTIFICATION_TYPES.ERROR,
              }))
              this.isDeletingImage = false
            })
        },
      }))
    } else {
      this._toUploadQueue = filter(this._toUploadQueue, obj => !isEqual(uploadFileHolder.name, obj.name))
    }
  }

  onProgress(progress) {
    const totalLength = [...this._toUploadQueue, ...this._uploadedQueue].length

    let currIdx = this._uploadedQueue.length

    let totalProgress = Math.ceil(progress / totalLength + (currIdx * (100 / totalLength)))
    if (totalProgress >= 100) this._progress = 100
    else this._progress = totalProgress
  }

  onError(error, uploadFileHolder) {
    const normErr = normalizeError(error)
    if (includes([500, 503], normErr.statusCode)) {
      notify(new NotificationOptions({
        message: "Ocorreu um erro durante seu upload, por favor, tente novamente mais tarde. Se o problema persistir entre em contato conosco pelo chat.",
        duration: 4 * 3600 * 1000,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      this._toUploadQueue[0].uploadProgress = 0
      this._toUploadQueue[0].uploadStarted = false
      this._toUploadQueue[0].uploadCompleted = false
      this._uploading = false
    } else if (normErr instanceof NetworkError) {
      const retrySecs = 10
      notify(new NotificationOptions({
        message: `Foi detectado uma falha de conexão com a internet.
          O sistema tentará reconectar em ${retrySecs} segundos.
          Por favor, verifique sua internet.`,
        duration: (retrySecs * 1000) - 200,
        type: NOTIFICATION_TYPES.WARNING,
      }))
      if (this.retrySetTimeout) clearTimeout(this.retrySetTimeout)
      this.retrySetTimeout = setTimeout(() => {
        this.upload(uploadFileHolder)
      }, retrySecs * 1000)
    }
  }

  onComplete(uploadFileHolder) {
    remove(this._toUploadQueue, obj => isEqual(uploadFileHolder.name, obj.name))
    this._uploadedQueue.push(uploadFileHolder)

    if (isEmpty(this._toUploadQueue)) {
      this._uploading = false
      this.onCompleteCallback()
      return
    }

    if (this._uploadedQueue.length % IMAGES_PER_PAGE === 0)
      this.changePageCallback((this._uploadedQueue.length / IMAGES_PER_PAGE))

    this.next()
  }

  //  Getters
  get toUploadQueue() {
    return this._toUploadQueue
  }

  get complete() {
    return this._complete
  }

  get uploading() {
    return this._uploading
  }

  get loading() {
    return this._loading
  }

  get deleting() {
    return this._deleting
  }

  get progress() {
    return this._progress
  }

  get uploadedQueue() {
    return this._uploadedQueue
  }

  get canUpload() {
    return !(this.uploading || this.loading)
  }

  get fullFilesList() {
    return [...this.uploadedQueue, ...this.toUploadQueue]
  }

  get isFinishingStream() {
    return this.uploading && this.progress >= 100 && this.toUploadQueue.length === 1
  }

  // Setters

  set progress(value) {
    this._progress = value
  }

  set uploadedQueue(value) {
    this._uploadedQueue = value
  }

  set loading(value) {
    this._loading = value
  }
}
